import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface FormTextareaProps {
  label: string
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  error?: string
  touched?: boolean
  placeholder?: string
  required?: boolean
  disabled?: boolean
  rows?: number
  maxLength?: number
  helpText?: string
  autoResize?: boolean
}

export const FormTextarea: React.FC<FormTextareaProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  touched,
  placeholder,
  required = false,
  disabled = false,
  rows = 4,
  maxLength,
  helpText,
  autoResize = false
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const hasError = touched && error
  const characterCount = value.length

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e)
    
    if (autoResize) {
      e.target.style.height = 'auto'
      e.target.style.height = `${e.target.scrollHeight}px`
    }
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-end mb-1">
        <label 
          htmlFor={name}
          className={`block text-sm font-medium transition-colors duration-200 ${
            hasError ? 'text-red-600' : isFocused ? 'text-blue-600' : 'text-gray-700'
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {maxLength && (
          <span className={`text-xs transition-colors duration-200 ${
            characterCount > maxLength * 0.9 
              ? 'text-red-600' 
              : 'text-gray-500'
          }`}>
            {characterCount}/{maxLength}
          </span>
        )}
      </div>

      <textarea
        id={name}
        name={name}
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false)
          if (onBlur) onBlur(e)
        }}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={`
          w-full px-3 py-2 border rounded-lg transition-all duration-200 resize-none
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          ${hasError 
            ? 'border-red-500 focus:ring-2 focus:ring-red-200 focus:border-red-500' 
            : isFocused
              ? 'border-blue-500 focus:ring-2 focus:ring-blue-200'
              : 'border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-blue-200 focus:border-blue-500'
          }
          placeholder-gray-400
        `}
        style={autoResize ? { overflow: 'hidden' } : {}}
        aria-invalid={hasError ? 'true' : 'false'}
        aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : undefined}
      />

      {helpText && !hasError && (
        <p id={`${name}-help`} className="mt-1 text-sm text-gray-500">
          {helpText}
        </p>
      )}

      <AnimatePresence>
        {hasError && (
          <motion.p
            id={`${name}-error`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-1 text-sm text-red-600 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FormTextarea